/* eslint-disable @typescript-eslint/no-unused-vars -- it is what it is*/
import { FilmCategory } from '@vista-digital/ocapi-types/v1'
import type { OrderConfig } from '@vista-digital/occ-domain'
import { BookingFeeMode, TaxMode, TicketingMode } from '@vista-digital/occ-domain'
import type {
    OccAccessibilityText,
    OccAssets,
    OccFilmConfig,
    OccFilterConfig,
    OccMemberConfig,
    OccNotificationConfig,
} from '@vista-digital/occ-ui'
import { defaultOccAccessibilityText } from '@vista-digital/occ-ui/src/lib/context/occ/types/accessibility-text'
import { defaultOccAssets } from '@vista-digital/occ-ui/src/lib/context/occ/types/assets'
import type { OccFormFieldValidationConfig } from '@vista-digital/occ-ui/src/lib/context/occ/types/config/form/form-field-validation'
import type { OccLocaleConfig } from '@vista-digital/occ-ui/src/lib/context/occ/types/config/locale/locale'
import { defaultOccMemberConfig } from '@vista-digital/occ-ui/src/lib/context/occ/types/config/member/member'
import type { OccDisplayText } from '@vista-digital/occ-ui/src/lib/context/occ/types/display-text/display-text'
import { FieldFormatType } from '@vista-digital/occ-ui/src/lib/types/form/field-format-type'
import type { NestedPartial } from '@vista-digital/utilities'
import { cmsToOccDisplayText } from '../../utils/cmsToOccDisplayText'
import type { OccContextProps } from './occConfiguration.models'

// DOMAIN
export function getOccContextDisplayText(props: OccContextProps): OccDisplayText {
    return {
        film: {
            advanceBookingLabel: props.advanceBookingLabel,
            comingSoonLabel: props.comingSoonLabel,
            nowShowingLabel: props.nowShowingLabel,
            preBookNowLabel: props.preBookNowLabel,
            sponsoredLabel: props.sponsoredLabel,
            runtimeFormat: props.filmRuntimeFormat,
            addToWatchlistButtonLabel: props.addToWatchlistButtonLabel,
            removeFromWatchlistButtonLabel: props.removeFromWatchlistButtonLabel,
            failedToAddFilmToWatchlistMessage: props.failedToAddFilmToWatchlistMessage,
            failedToAddFilmToWatchlistTitle: props.failedToAddFilmToWatchlistTitle,
            failedToRemoveFilmFromWatchlistMessage: props.failedToRemoveFilmFromWatchlistMessage,
            failedToRemoveFilmFromWatchlistTitle: props.failedToRemoveFilmFromWatchlistTitle,
        },
        // Maybe in the CMS, probably not
        seat: {
            labelFormat: '${rowLabel}-${seatLabel}',
            groupFormat: '${firstSeatLabel} a ${lastSeatLabel}',
            groupSeparator: ', ',
            placeholderLabel: {
                text: '— ',
                className: 'v-seat-summary-placeholder-label',
            },
            areaNameSeparator: ', ',
        },
        form: {
            fieldOptionalSuffix: props.formFieldOptionalSuffix,
            passwordRuleCheckerHelpText: props.passwordRuleCheckerHelpText,
            cancelButtonLabel: props.cancelButtonLabel,
            submitButtonLabel: props.submitButtonLabel,
        },
        order: {
            bookingFeeLabel: cmsToOccDisplayText(props.bookingFeeLabel),
            includedBookingFeeLabel: cmsToOccDisplayText(props.includedBookingFeeLabel),
            itemLimitReachedMessage: props.itemLimitReachedMessage,
            inProgressItemLimitReachedMessage: props.inProgressItemLimitReachedMessage,
            estimatedTaxLabel: props.estimatedTaxLabel,
            inProgressItemRedemptionLimitReachedMessage: props.inProgressItemRedemptionLimitReachedMessage,
            subtotalCostExcludingBookingFeeLabel: props.subtotalCostExcludingBookingFeeLabel,
            subtotalCostIncludingBookingFeeLabel: cmsToOccDisplayText(props.subtotalCostIncludingBookingFeeLabel),
            taxLabel: props.taxLabel,
            totalCostLabel: props.totalCostLabel,
            totalSavingsLabel: props.totalSavingsLabel,
            tipPercentageLabel: props.tipPercentageLabel,
            tipPercentageFormat: props.tipPercentageFormat,
            tipLabel: props.tipLabel,
            zeroTipLabel: props.zeroTipLabel,
            changeTipButtonLabel: props.changeTipButtonLabel,
            patronCommentFormat: props.patronCommentFormat,
        },
        showtime: {
            soldOutLabel: props.soldOutLabel,
        },
        filter: {
            allOptionLabel: props.allOptionLabel,
            nonRecurringSubscriptionBillingPeriodLabel: props.nonRecurringSubscriptionBillingPeriodLabel,
            recurringSubscriptionBillingPeriodLabel: props.recurringSubscriptionBillingPeriodLabel,
            oneMonthSubscriptionBillingPeriodLabel: props.oneMonthSubscriptionBillingPeriodLabel,
            subscriptionBillingPeriodLabel: props.subscriptionBillingPeriodLabel,
        },
        member: {
            rewardExclusiveLabel: props.rewardExclusiveLabel,
            defaultBalanceTypeName: props.defaultBalanceTypeName,
            defaultBalanceTypeUnitName: props.defaultBalanceTypeUnitName,
            friendCodeFormat: props.friendCodeFormat,
            memberBalanceFormat: props.memberBalanceFormat,
        },
        price: {
            discountedPriceFormat: props.discountedPriceFormat,
            freeDiscountLabel: props.freeDiscountLabel,
            itemPriceFormat: props.itemPriceFormat,
            percentageOffDiscountFormat: props.percentageOffDiscountFormat,
            priceOffDiscountFormat: props.priceOffDiscountFormat,
            soldOutLabel: props.soldOutLabel,
            availableFromTimeLabel: props.availableFromTimeLabel,
            availableFromDayLabel: props.availableFromDayLabel,
            unavailableForShowtimeLabel: props.unavailableForShowtimeLabel,
        },
        subscription: {
            recurringOneMonthBillingPeriodLabel: props.recurringOneMonthBillingPeriodLabel,
            recurringTwelveMonthsBillingPeriodLabel: props.recurringTwelveMonthsBillingPeriodLabel,
            recurringBillingPeriodLabel: props.recurringBillingPeriodLabel,
            nonRecurringOneMonthBillingPeriodLabel: props.nonRecurringOneMonthBillingPeriodLabel,
            nonRecurringBillingPeriodLabel: props.nonRecurringBillingPeriodLabel,
        },

        cinemaChainName: props.cinemaChainName,
        componentErrorTitle: props.componentErrorTitle,
        noSearchResultsFoundMessage: props.noSearchResultsFoundMessage,
    }
}

// CONFIG
export function getOccLocale(props: OccContextProps): NestedPartial<OccLocaleConfig> {
    return {
        currency: {
            symbol: props.symbol,
            decimalPrecision: props.decimalPrecision,
            decimalSeparator: props.decimalSeparator,
            format: props.format,
            nonNumberPlaceholder: props.nonNumberPlaceholder,
            thousandsSeparator: props.thousandsSeparator,
        },
        date: {
            formats: {
                dateDefault: props.dateDefault,
                dateLong: props.dateLong,
                dateShort: props.dateShort,
                iso: props.iso,
                timeDefault: props.timeDefault,
                timeLong: props.timeLong,
                timeShort: props.timeShort,
            },
            startWeekOnSunday: props.startWeekOnSunday,
            ordinalStringFormatter: (value: number) => {
                // Todo, redo ordinals for spain and portugal

                // const absValue = Math.abs(value);
                // const rem10 = absValue % 10;
                // const rem100 = absValue % 100;
                // if (rem10 === 1 && rem100 !== 11) {
                //     return value + 'sto';
                // }
                // if (rem10 === 2 && rem100 !== 12) {
                //     return value + 'ndo';
                // }
                // if (rem10 === 3 && rem100 !== 13) {
                //     return value + 'rdo';
                // }
                // return value + 'tho';

                return value
            },
        },
        // TODO: number
        // TODO: personName
    }
}

export function getOrderConfig(props: OccContextProps): OrderConfig {
    return {
        admittingDuration: props.admittingDuration,
        bookingFeeMode: props.bookingFeeExclusive ? BookingFeeMode.Exclusive : BookingFeeMode.Inclusive,
        isBookingFeeApplicableToUnpaidBookings: props.isBookingFeeApplicableToUnpaidBookings,
        maximumTicketsPerOrder: props.maximumTicketsPerOrder,
        ticketingMode: props.isSeatFirstOrdering ? TicketingMode.SeatFirst : TicketingMode.TicketFirst,
        unpaidBookingCutoffTime: props.unpaidBookingCutoffTime,
        taxMode: props.taxModeExclusive ? TaxMode.Exclusive : TaxMode.Inclusive,
        maximumItemsPerOrder: props.maximumItemsPerOrder,
        maximumModifiersPerItem: props.maximumModifiersPerItem,
        tip: {
            isEnabled: props.tipIsEnabled,
            includeSundryItems: props.tipIncludeSundryItems,
        },
        redemptionExpiringSoonDurationInDays: props.redemptionExpiringSoonDurationInDays,
    }
}

export function getFormValidationConfig(
    displayText: OccContextProps,
    config?: NestedPartial<OccFormFieldValidationConfig>
): NestedPartial<OccFormFieldValidationConfig> {
    return {
        password: {
            type: FieldFormatType.Password,
            required: true,
            maxLength: 64,
            rules: [
                {
                    displayText: displayText.passwordRuleCheckerUppercaseRule,
                    regExpression: '([A-Z])',
                },
                {
                    displayText: displayText.passwordRuleCheckerLowercaseRule,
                    regExpression: '([a-z])',
                },
                {
                    displayText: displayText.passwordRuleCheckerNumericRule,
                    regExpression: '([0-9])',
                },
                {
                    displayText: displayText.passwordRuleCheckerSpecialCharacterRule,
                    regExpression:
                        '[\'\\/\\!\\@#\\$\\%\\^\\&\\*\\)\\(\\+\\=\\.\\<\\>\\{\\}\\[\\]\\:\\;\\|\\~\\`\\_\\-,\\?\\¬£"]',
                },
                {
                    displayText: displayText.passwordRuleCheckerMinLengthRule,
                    regExpression: '.{10,}',
                },
            ],
        },
        ...config,
    }
}

export function getFilmConfig(props: OccContextProps): OccFilmConfig {
    return {
        customActions: {},
        showWatchlistButtons: props.showWatchlistButtons,
    }
}

export function getFilterConfig(props: OccContextProps): OccFilterConfig {
    return {
        attributeIds: undefined,
        showFullAttributeName: false,
        filmCategories: [FilmCategory.NowShowing, FilmCategory.ComingSoon, FilmCategory.AdvanceBooking],
    }
}

export function getMemberConfig(props: OccContextProps): OccMemberConfig {
    return {
        ...defaultOccMemberConfig,
    }
}

export function getNotificationConfig(props: OccContextProps): OccNotificationConfig {
    return {
        dismissalTime: props.notificationDismissalTime,
    }
}

// ASSETS
export function getAssets(props: OccContextProps): OccAssets {
    return {
        ...defaultOccAssets,
        componentErrorIconName: props.componentErrorIconName,
        filmWatchlistButtonIconName: props.filmWatchlistButtonIconName,
        loadingIconName: props.loadingIconName,
        modalCloseButtonIconName: props.modalCloseButtonIconName,

        passwordInputHiddenIconName: props.passwordInputHiddenIconName,
        passwordInputVisibleIconName: props.passwordInputVisibleIconName,
        passwordRuleCheckerInitialIconName: props.passwordRuleCheckerInitialIconName,
        passwordRuleCheckerInvalidIconName: props.passwordRuleCheckerInvalidIconName,
        passwordRuleCheckerSuccessIconName: props.passwordRuleCheckerSuccessIconName,
        advanceBookingPeriodNotificationIconName: props.advanceBookingPeriodNotificationIconName,

        // expandContentIndicatorIconName:,
        // notificationCloseButtonIconName:,
        // notificationErrorIconName:,
        // notificationInfoIconName:,
        // notificationSuccessIconName:,
        // notificationWarningIconName:,
    }
}

// ACCESSIBILITY
export function getOccAccessibilityText(props: OccContextProps): OccAccessibilityText {
    return {
        ...defaultOccAccessibilityText,

        itemImageAltTextFormat: props.itemImageAltTextFormat,

        eventGalleryImageAltTextFormat: props.eventGalleryImageAltTextFormat,
        eventPosterAltTextFormat: props.eventPosterAltTextFormat,
        filmBackdropAltTextFormat: props.filmBackdropAltTextFormat,
        filmPosterAltTextFormat: props.filmPosterAltTextFormat,
        filmStillAltTextFormat: props.filmStillAltTextFormat,
        memberPhotoAltTextFormat: props.memberPhotoAltTextFormat,
        memberRewardImageAltTextFormat: props.memberRewardImageAltTextFormat,
        modalCloseButtonAriaLabel: props.modalCloseButtonAriaLabel,
        redirectPaymentMethodIconAltTextFormat: props.redirectPaymentMethodIconAltTextFormat,

        // Seat picker
        filmAdvertisementAltTextFormat: props.filmAdvertisementAltTextFormat,
        filmTitleTreatmentAltTextFormat: props.filmTitleTreatmentAltTextFormat,
        memberPhotoWithoutNameAltTextFormat: props.memberPhotoWithoutNameAltTextFormat,
        redirectPaymentMethodButtonAriaLabelFormat: props.redirectPaymentMethodButtonAriaLabelFormat,
        removeGiftCardButtonAriaLabelFormat: props.removeGiftCardButtonAriaLabelFormat,
        removeMemberBalancePaymentButtonAriaLabelFormat: props.removeMemberBalancePaymentButtonAriaLabelFormat,
        saveToGooglePayAriaLabelFormat: props.saveToGooglePayAriaLabelFormat,
        addToAppleWalletButtonAriaLabelFormat: props.addToAppleWalletButtonAriaLabelFormat,
        companionSeatAriaLabelFormat: props.companionSeatAriaLabelFormat,
        sofaLeftSeatAriaLabelFormat: props.sofaLeftSeatAriaLabelFormat,
        sofaMiddleSeatAriaLabelFormat: props.sofaMiddleSeatAriaLabelFormat,
        sofaRightSeatAriaLabelFormat: props.sofaRightSeatAriaLabelFormat,
        unavailableSeatAriaLabelFormat: props.unavailableSeatAriaLabelFormat,
        wheelchairSeatAriaLabelFormat: props.wheelchairSeatAriaLabelFormat,
        normalSeatAriaLabelFormat: props.normalSeatAriaLabelFormat,
    }
}
